/**
 * DateTimeDisplay Component
 * 
 * Read-only formatted date/time display component with various formats
 * for different contexts (event cards, headers, activity feed, etc.)
 */

import { formatEventDate, formatEventTime, formatCompactDate, formatCompactTime, formatDateRange, getRelativeTime } from '../../lib/datetime';

interface DateTimeDisplayProps {
  timestamp: number;
  endTimestamp?: number;
  format?: 'full' | 'compact' | 'time-only' | 'date-only' | 'relative' | 'range';
  className?: string;
  showRelative?: boolean;
}

export function DateTimeDisplay({
  timestamp,
  endTimestamp,
  format = 'compact',
  className = '',
  showRelative = false
}: DateTimeDisplayProps) {
  const formatDateTime = () => {
    switch (format) {
      case 'full':
        return `${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`;
      
      case 'compact':
        return `${formatCompactDate(timestamp)} at ${formatCompactTime(timestamp)}`;
      
      case 'time-only':
        return formatEventTime(timestamp);
      
      case 'date-only':
        return formatEventDate(timestamp);
      
      case 'relative':
        return getRelativeTime(timestamp);
      
      case 'range':
        return formatDateRange(timestamp, endTimestamp);
      
      default:
        return formatCompactDate(timestamp);
    }
  };

  const relativeTime = showRelative ? getRelativeTime(timestamp) : null;

  return (
    <time 
      dateTime={new Date(timestamp * 1000).toISOString()}
      className={`text-text-secondary ${className}`}
      title={`${formatEventDate(timestamp)} at ${formatEventTime(timestamp)}`}
    >
      {formatDateTime()}
      {relativeTime && (
        <span className="text-text-muted text-sm ml-2">
          ({relativeTime})
        </span>
      )}
    </time>
  );
}

/**
 * Specialized variants for common use cases
 */

// For event cards - compact with relative time
export function EventDateTime({ timestamp, endTimestamp, className }: Omit<DateTimeDisplayProps, 'format'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      endTimestamp={endTimestamp}
      format="range"
      showRelative
      className={className}
    />
  );
}

// For activity feed - just relative time
export function ActivityDateTime({ timestamp, className }: Omit<DateTimeDisplayProps, 'format' | 'endTimestamp'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      format="relative"
      className={`text-text-muted text-sm ${className}`}
    />
  );
}

// For chat messages - just time
export function ChatDateTime({ timestamp, className }: Omit<DateTimeDisplayProps, 'format' | 'endTimestamp'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      format="time-only"
      className={`text-text-muted text-xs ${className}`}
    />
  );
}

// For event headers - full format
export function EventHeaderDateTime({ timestamp, endTimestamp, className }: Omit<DateTimeDisplayProps, 'format'>) {
  return (
    <DateTimeDisplay 
      timestamp={timestamp}
      endTimestamp={endTimestamp}
      format="full"
      className={`text-text-primary font-medium ${className}`}
    />
  );
}